import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Percent, Loader2, KeyRound, Lock, CreditCard, ShieldCheck } from "lucide-react";
import { fmtTL } from "@/components/StatusBadge";

const STANDARD_RATES = [3, 5, 8, 10];

/**
 * İskonto Uygulama — iki mod:
 *  • Yönetici (isAdmin): oranı GİRER veya hazır oranlardan seçer (self-authorize).
 *  • Diğer roller (kasiyer / hızlı satış): ORAN GİREMEZ. Yalnızca amir PIN / kart / şifre okutur;
 *    sistem amirin rolüne TANIMLI iskonto oranını OTOMATİK uygular + haftalık kotayı denetler.
 */
export default function DiscountAuthDialog({ open, gross = 0, isAdmin = false, onClose, onApplied }) {
  const [rate, setRate] = useState("");
  const [method, setMethod] = useState("pin");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [card, setCard] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setRate(""); setPin(""); setPassword(""); setEmail(""); setCard(""); setMethod("pin"); }
  }, [open]);

  const pct = Math.min(Math.max(0, Number(rate) || 0), 100);
  const previewAmount = gross > 0 ? Math.min(gross, gross * pct / 100) : 0;

  // Yönetici — kendi (sınırsız) yetkisiyle istediği oranı uygular
  const applyAdminRate = async () => {
    if (pct <= 0) { toast.error("Geçerli bir iskonto oranı girin (%0'dan büyük)."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/pos/discount/self-authorize", { pct });
      onApplied(data.token, data.authorized_by, data.pct);
      toast.success(`İskonto uygulandı · %${data.pct} · ${data.authorized_by?.name || "onaylı"}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "İskonto uygulanamadı");
    } finally { setLoading(false); }
  };

  // Kasiyer / hızlı satış — amir kimliği okutulur, rolüne TANIMLI oran OTOMATİK uygulanır
  const applyManager = async () => {
    const body = {};
    if (method === "pin") { if (!pin) return toast.error("Amir PIN girin"); body.pin = pin; }
    else if (method === "password") { if (!password) return toast.error("Amir şifresi girin"); body.password = password; if (email) body.email = email; }
    else if (method === "card") { if (!card) return toast.error("Amir kartını okutun"); body.card = card; }
    setLoading(true);
    try {
      const { data } = await api.post("/pos/discount/manager-authorize", body);
      onApplied(data.token, data.authorized_by, data.pct);
      toast.success(`İskonto uygulandı · %${data.pct}`);
    } catch (err) {
      const st = err.response?.status;
      if (st === 409) toast.warning("Bu PIN birden fazla kullanıcıya ait. Lütfen e-posta+şifre veya kart ile onaylayın.", { duration: 5000 });
      else if (st === 403) toast.error(apiError(err.response?.data?.detail) || "Bu amirin iskonto yetkisi yok ya da haftalık hakkı dolmuş.", { duration: 5000 });
      else toast.error(apiError(err.response?.data?.detail) || "İskonto onayı reddedildi");
    } finally { setLoading(false); setPin(""); setPassword(""); setCard(""); }
  };

  const Tab = ({ id, icon: Icon, label }) => (
    <button type="button" data-testid={`disc-method-${id}`} onClick={() => setMethod(id)}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${method === id ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="discount-auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Percent className="h-5 w-5 text-brand-orange" /> İskonto Uygula
          </DialogTitle>
          <DialogDescription>
            {isAdmin
              ? "Yönetici: oranı yazın veya hazır oranlardan seçin."
              : "Amir onayı ile uygulanır."}
          </DialogDescription>
        </DialogHeader>

        {isAdmin ? (
          <div className="space-y-3" data-testid="disc-admin-mode">
            <div>
              <label className="overline">İskonto Oranı (%)</label>
              <div className="mt-1 flex items-center gap-2">
                <div className="relative flex-1">
                  <Input data-testid="disc-rate-input" type="number" min="0" max="100" step="0.5" inputMode="decimal"
                    value={rate} autoFocus autoComplete="off"
                    onChange={(e) => setRate(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyAdminRate()}
                    placeholder="Örn. 15" className="pr-7 text-center text-lg font-bold tabular" />
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">%</span>
                </div>
              </div>
            </div>

            <div>
              <label className="overline">Hazır Oranlar</label>
              <div className="mt-1 grid grid-cols-4 gap-2">
                {STANDARD_RATES.map((r) => (
                  <button key={r} type="button" data-testid={`disc-rate-chip-${r}`}
                    onClick={() => setRate(String(r))}
                    className={`rounded-md py-2 text-sm font-bold transition-colors ${Number(rate) === r ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                    %{r}
                  </button>
                ))}
              </div>
            </div>

            {pct > 0 && gross > 0 && (
              <div data-testid="disc-preview" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600 ring-1 ring-inset ring-red-200">
                Bu satışta indirim: <b>−{fmtTL(previewAmount)}</b> (ara toplam {fmtTL(gross)})
              </div>
            )}

            <Button data-testid="disc-rate-apply" onClick={applyAdminRate} disabled={loading || pct <= 0}
              className="w-full bg-brand-orange text-white hover:bg-orange-600">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Uygula
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="disc-manager-mode">
            <div className="flex gap-2">
              <Tab id="pin" icon={KeyRound} label="PIN" />
              <Tab id="password" icon={Lock} label="Şifre" />
              <Tab id="card" icon={CreditCard} label="Kart" />
            </div>
            {method === "pin" && (
              <div><label className="overline">Amir PIN</label>
                <Input data-testid="disc-pin" type="password" inputMode="numeric" value={pin} autoFocus autoComplete="new-password"
                  onChange={(e) => setPin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyManager()}
                  placeholder="****" className="mt-1 tabular text-center text-lg tracking-widest" /></div>
            )}
            {method === "password" && (
              <>
                <div><label className="overline">E-posta (opsiyonel)</label>
                  <Input data-testid="disc-email" value={email} autoComplete="off" onChange={(e) => setEmail(e.target.value)} placeholder="amir@groot.com" className="mt-1" /></div>
                <div><label className="overline">Amir Şifresi</label>
                  <Input data-testid="disc-password" type="password" value={password} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyManager()} placeholder="••••••" className="mt-1" /></div>
              </>
            )}
            {method === "card" && (
              <div><label className="overline">Amir Kart Kodu</label>
                <Input data-testid="disc-card" value={card} autoFocus autoComplete="off" onChange={(e) => setCard(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyManager()}
                  placeholder="Kartı okutun / kodu girin" className="mt-1 font-mono" /></div>
            )}
            <Button data-testid="disc-submit" onClick={applyManager} disabled={loading}
              className="w-full bg-brand-orange text-white hover:bg-orange-600">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Amir Onayı ile Uygula
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
